import * as logger from "../utils/logger";
import { DeepPartial } from "../lib/redux";
import { defaultData } from "./data/reducers";
import { defaultMobile } from "./mobile/reducers";
import { IDataStore } from "./data/types";
import storage, { StorageKeys } from "../utils/storage/storage";
import { defaultUsers } from "./users/reducers";
import DutyStatusLog, { IAnnotation } from "../model/DutyStatusLog";
import { IHistoricalDayTracker } from "./hosLogs/types";
import UserHosRuleSet from "../model/UserHosRuleSet";
import Trip from "../model/Trip";
import { defaultTrips } from "./trips/reducers";
import TripAnnotation from "../model/TripAnnotation";
import GroupLinkedDevice from "../model/GroupLinkedDevice";
import { defaultGroupLinkedDevices } from "./groupLinkedDevice/reducers";
import { IDVIRLog } from "../model/DVIRLog";
import { defaultFeedVersions } from "./feedVersions/reducers";
import { ITimeZoneInfo } from "../utils/date";
import { IDailyOdometer } from "../model/DutyStatusLogs";
import { defaultDailyOdometer } from "./dailyOdometer/reducers";
import { IDefect } from "../model/Defect";
import { defaultDefectLists } from "./defectLists/reducers";
import { defaultTranslations } from "./translation/reducers";
import { IMessage, IRoute } from "../model/Message";
import { defaultMessages } from "./messages/reducers";
import { IDriverChange } from "../model/DriverChangeLog";
import { defaultRoutes } from "./routes/reducers";
import { ITrailerAttachment } from "../model/TrailerAttachment";
import { defaultTrailerAttachments } from "./trailerAttachments/reducers";
import { IDeviceLink } from "../model/DeviceLink";
import { IShipment } from "../model/Shipment";
import { ISuspiciousLogsSent } from "./suspiciousLogsSent/types";
import { defaultSuspiciousLogSent } from "./suspiciousLogsSent/reducers";
import { ICacheStore } from "./cache/types";
import { ICredentialsWithServer } from "../utils/credentialsStore";
import { defaultOpenCabUsers } from "./openCabUsers/reducers";
import { IOpenCabWorkflow } from "./openCabWorkflow/types";
import { defaultOpenCabWorkflow } from "./openCabWorkflow/reducers";
import { IamCredentials } from "./iamCredentials/actions";
import { ISystemSettings } from "./systemSettings/types";
import { defaultSystemSettings } from "./systemSettings/reducers";
import { defaultOpenCabQueue } from "./openCabQueue/reducers";
import { OpenCabQueue } from "./openCabQueue/types";
import { defaultVehicleHistory } from "./vehicleHistory/reducers";
import {IVehicleHistoryStore} from "./vehicleHistory/types";
import { getModuleStateData } from "../modules/reducers";
import { defaultScheduleStatus } from "./scheduleStatus/reducers";
import { getRepairNeededByUser } from "../model/dvirRepair";
import { ITranslations } from "../model/Translation";
import { defaultDriverSeatUser } from "./driverSeat/reducers";
import { GlobalReducer } from "./reducers";
import { defaultHosRuleResults } from "./hosRuleResults/reducers";
import { getItem, isNativeStorageAvailable } from "../utils/native/secureStorageApi";
import { IError } from "./error/types";
import ModuleStorageKeys from "../modules/storageKeys";
import { IndexedDbStorage } from "../utils/storage/indexedDbStorage";
import { DRIVE_STORAGE_PREFIX } from "./storageConstants";
import IndexedDbMigrationFromRedux from "../utils/storage/indexedDbMigrationFromRedux";
import { REDUCER_KEYS_STILL_IN_LOCALSTORAGE } from "./reducerPersistence";

/**
 * CRITICAL FUNCTION: Responsible for preloading the entire store from storage.
 * Handles errors internally: logs them and returns an empty store so the app can still start.
 */
export async function preload(): Promise<DeepReadonly<GlobalReducer>> {
    const isIndexedDb = IndexedDbMigrationFromRedux.isIndexedDbInUse;
    let dataFromIndexedDb: DeepPartial<GlobalReducer> = {};
    const storageType = isIndexedDb ? "IndexedDb" : "localStorage";

    logger.log(logger.Source.STORAGE, `Preloading store from ${ storageType }`);

    if (isIndexedDb) {
        dataFromIndexedDb = await IndexedDbStorage.getEverythingFromIndexedDb();
    }

    const retrieveFromStorage = <T>(key: StorageKeys | ModuleStorageKeys, defaultValue: T): T => {
        if (isIndexedDb) {
            const fullKey = `${ DRIVE_STORAGE_PREFIX }${ key }`;
            // Some reducers are dual-written to both localStorage (sync) and IndexedDB (async)
            // via REDUCER_KEYS_STILL_IN_LOCALSTORAGE. For those keys, localStorage is always at
            // least as fresh as IndexedDB, so check it first to avoid reading stale data when an
            // async IndexedDB write was interrupted (e.g. by a database redirect before the
            // transaction committed).
            if (REDUCER_KEYS_STILL_IN_LOCALSTORAGE.has(key)) {
                let jsonParse = true;
                if (key === StorageKeys.DRIVER_SEAT_USER) {
                    jsonParse = false;
                }
                const lsValue = storage.get<T>(fullKey, jsonParse, undefined, true);
                if (lsValue) {
                    return lsValue;
                }
            }
            return dataFromIndexedDb[fullKey] || defaultValue;
        } else {
            let jsonParse = true;
            if (key === StorageKeys.DRIVER_SEAT_USER) {
                jsonParse = false;
            }
            return storage.get<T>(`${ DRIVE_STORAGE_PREFIX }${ key }`, jsonParse, undefined, true) || defaultValue;
        }
    };

    const retrieveSecureStorageValue = async <T>(storageKey: StorageKeys | ModuleStorageKeys): Promise<T> => {
        // unsupported device only read from web storage available
        if (!isNativeStorageAvailable()) {
            if (isIndexedDb) {
                const idbKey = `${ DRIVE_STORAGE_PREFIX }${ storageKey }`;
                // Only check localStorage first for keys that are dual-written via
                // REDUCER_KEYS_STILL_IN_LOCALSTORAGE. For those keys, localStorage is always at
                // least as fresh as IndexedDB, so prefer it to avoid reading stale data when an
                // async IndexedDB write was interrupted (e.g. by a database redirect).
                if (REDUCER_KEYS_STILL_IN_LOCALSTORAGE.has(storageKey)) {
                    const lsValue = storage.get<T>(idbKey, true, undefined, true);
                    if (lsValue) {
                        return lsValue;
                    }
                }
                return dataFromIndexedDb[idbKey] as T || {} as T;
            }
            return retrieveFromStorage<T>(storageKey, {} as T);
        }

        const res = await getItem<T>(storageKey, true);
        if (res) {
            return res;
        }

        // no fallback to local storage
        return {} as T;
    };

    let result: DeepReadonly<GlobalReducer>;
    try {
        const storedDvirLogs = retrieveFromStorage<IIdDictionary<IIdDictionary<IDVIRLog>>>(StorageKeys.DVIR_KEY, {});
        result = {
            driveAPICredentials: await retrieveSecureStorageValue<IIdDictionary<ICredentialsWithServer>>(StorageKeys.DRIVE_API_CREDENTIALS_KEY),
            iamCredentials: await retrieveSecureStorageValue<IIdDictionary<IamCredentials>>(StorageKeys.IAM_CREDENTIALS_KEY),

            deviceMovement: {},
            activeUsers: retrieveFromStorage<DeepReadonlyArray<string>>(StorageKeys.ACTIVE_USERS_KEY, []),
            cache: {
                ...retrieveFromStorage<ICacheStore>(StorageKeys.CACHE_KEY, {})
            },
            mobile: defaultMobile,
            hosRuleResults: defaultHosRuleResults,
            data: {
                ...defaultData,
                ...retrieveFromStorage<IDataStore>(StorageKeys.DATA_KEY, defaultData),
                // The DriveNative host owns this and pushes it again on every load. A stored value would keep
                // overriding the web app's own driving value after a restart where the host no longer pushes it.
                nativeMotion: undefined
            },
            hosAnnotations: retrieveFromStorage<IIdDictionary<IIdDictionary<IAnnotation>>>(StorageKeys.HOSANNOTATIONS_KEY, {}),
            historicalDayTracker: retrieveFromStorage<IHistoricalDayTracker>(StorageKeys.HISTORICAL_DAY_TRACKER_KEY, {}),
            users: retrieveFromStorage<IIdDictionary<IUser>>(StorageKeys.USERS_KEY, defaultUsers),
            userHosLogs: retrieveFromStorage<IIdDictionary<IIdDictionary<DutyStatusLog>>>(StorageKeys.USER_LOGS_KEY, {}),
            systemHosLogs: retrieveFromStorage<IIdDictionary<IIdDictionary<DutyStatusLog>>>(StorageKeys.SYSTEM_LOGS_KEY, {}),
            userHosRuleSets: retrieveFromStorage<IIdDictionary<IIdDictionary<UserHosRuleSet>>>(StorageKeys.USER_HOS_RULESETS_KEY, {}),
            dvirLogs: storedDvirLogs,
            dvirRepairNeeded: getRepairNeededByUser(storedDvirLogs),
            // Trips are not seeded: a count stored from an earlier day would be wrong until the next trips sync.
            tripsTodayCount: {},
            trips: retrieveFromStorage<IIdDictionary<IIdDictionary<Trip>>>(StorageKeys.USER_TRIPS_KEY, defaultTrips),
            tripAnnotations: retrieveFromStorage<IIdDictionary<IIdDictionary<TripAnnotation>>>(StorageKeys.TRIPANNOTATIONS_KEY, {}),
            groupLinkedDevices: retrieveFromStorage<IIdDictionary<IIdDictionary<GroupLinkedDevice>>>(StorageKeys.GROUPLINKEDDEVICES_KEY, defaultGroupLinkedDevices),
            feedVersions: retrieveFromStorage<IIdDictionary<IIdDictionary<string | IIdDictionary<string>>>>(StorageKeys.FEEDVERSION_KEY, defaultFeedVersions),
            timeZoneInfo: retrieveFromStorage<IIdDictionary<ITimeZoneInfo>>(StorageKeys.TIME_ZONE_INFO_KEY, {}),
            dailyOdometer: retrieveFromStorage<IIdDictionary<IDailyOdometer>>(StorageKeys.DAILY_ODOMETER_KEY, defaultDailyOdometer),
            messages: retrieveFromStorage<IIdDictionary<IIdDictionary<IMessage>>>(StorageKeys.MESSAGES_KEY, defaultMessages),
            // Message revision is an in-memory bridge signal, not persisted content.
            messageRevision: 0,
            driverChanges: retrieveFromStorage<IIdDictionary<IDriverChange[]>>(StorageKeys.DRIVER_CHANGES_KEY, {}),
            deviceDefectLists: retrieveFromStorage<IIdDictionary<IIdDictionary<IDefect[]>>>(StorageKeys.DEVICE_DEFECTLISTS_KEY, defaultDefectLists),
            trailerDefectLists: retrieveFromStorage<IIdDictionary<IIdDictionary<IDefect[]>>>(StorageKeys.TRAILER_DEFECTLISTS_KEY, defaultDefectLists),
            routes: retrieveFromStorage<IIdDictionary<IIdDictionary<IRoute>>>(StorageKeys.ROUTES_KEY, defaultRoutes),
            error: retrieveFromStorage<IError>(StorageKeys.ERROR_KEY, {}),
            trailerAttachments: retrieveFromStorage<IIdDictionary<IIdDictionary<ITrailerAttachment>>>(StorageKeys.TRAILER_ATTACHMENTS_KEY, defaultTrailerAttachments),
            deviceLinks: retrieveFromStorage<IIdDictionary<IIdDictionary<IDeviceLink>>>(StorageKeys.DEVICE_LINKS_KEY, {}),
            shipments: retrieveFromStorage<IIdDictionary<IIdDictionary<IShipment>>>(StorageKeys.SHIPMENTS_KEY, {}),
            suspiciousLogsSent: retrieveFromStorage<ISuspiciousLogsSent>(StorageKeys.SUSPICIOUS_LOG_KEY, defaultSuspiciousLogSent),
            openCabUsers: retrieveFromStorage<IIdDictionary<string>>(StorageKeys.OPENCAB_USERS_KEY, defaultOpenCabUsers),
            openCabWorkflow: retrieveFromStorage<IOpenCabWorkflow>(StorageKeys.OPENCAB_WORKFLOW_KEY, defaultOpenCabWorkflow),
            openCabQueue: retrieveFromStorage<DeepReadonlyArray<OpenCabQueue>>(StorageKeys.OPENCAB_QUEUE_KEY, defaultOpenCabQueue),
            systemSettings: retrieveFromStorage<ISystemSettings>(StorageKeys.SYSTEMSETTINGS_KEY, defaultSystemSettings),
            vehicleHistory: retrieveFromStorage<IVehicleHistoryStore>(StorageKeys.VEHICLE_HISTORY_KEY, defaultVehicleHistory),
            translations: retrieveFromStorage<ITranslations>(StorageKeys.TRANSLATIONS_KEY, defaultTranslations),
                scheduleStatus: defaultScheduleStatus,
            driverSeatUser: retrieveFromStorage<string>(StorageKeys.DRIVER_SEAT_USER, defaultDriverSeatUser),
            ...await getModuleStateData((key, defaultValue) => retrieveFromStorage<any>(key, defaultValue))
        };
        logger.log(logger.Source.STORAGE, `Preloaded store from ${ storageType } successfully`);
    } catch (e) {
        logger.error(logger.Source.STORAGE, `Error preloading store from ${ storageType }: ${ e }`);
        result = {} as DeepReadonly<GlobalReducer>;
    }
    return result;
}